<template>
  <VTDropdownPill
    :disable-save-button="isSelectedActiveTimeInvalid()"
    :is-enabled="!disabled"
    @confirm="saveChanges"
    @clear="reset"
    @on-click-outside="discardChanges"
  >
    <template #dropDownHeader>
      <VTPill label="Active time">
        <template #values>
          <div :class="$style.wrapper">
            <div>
              <div :class="$style.row">
                <div :class="[$style.column, $style.timeText]">
                  {{ props.modelValue.from }}
                </div>
                <div :class="[$style.column, $style.timeText]">-</div>
                <div :class="[$style.column, $style.timeText]">{{ props.modelValue.to }}</div>
              </div>
            </div>
          </div>
        </template>
      </VTPill>
    </template>
    <template #dropdown-content>
      <div :class="$style.dropdownContainer">
        <p>Active time</p>
        <div :class="$style.row">
          <div :class="$style.column">
            <label for="activeStartTime">From</label>
            <DatePicker
              v-if="startTimeCalendarParentRef"
              v-model="activeTime.from"
              time-only
              icon-display="input"
              show-icon
              show-time
              input-id="activeStartTime"
              append-to="#startTimeCalendarParent"
              :invalid="isSelectedActiveTimeInvalid()"
              :pt="{
                panel: { class: $style.fixedTopLeft },
              }"
            >
              <template #inputicon="{ clickCallback }">
                <i class="pi pi-clock cursor-pointer" @click="clickCallback" />
              </template>
            </DatePicker>
            <div id="startTimeCalendarParent" ref="startTimeCalendarParentRef"></div>
          </div>
          <div :class="$style.column">
            <label for="activeEndTime">To</label>
            <DatePicker
              v-if="endTimeCalendarParentRef"
              v-model="activeTime.to"
              time-only
              icon-display="input"
              show-icon
              show-time
              input-id="activeEndTime"
              append-to="#endTimeCalendarParent"
              :invalid="isSelectedActiveTimeInvalid()"
              :pt="{
                panel: { class: $style.fixedTopLeft },
              }"
            >
              <template #inputicon="{ clickCallback }">
                <i class="pi pi-clock cursor-pointer" @click="clickCallback" />
              </template>
            </DatePicker>
            <div id="endTimeCalendarParent" ref="endTimeCalendarParentRef"></div>
          </div>
        </div>
        <div v-if="isSelectedActiveTimeInvalid()">
          <p :class="$style.invalidText">The start time should be before the end time!</p>
        </div>
        <div :class="$style.row">
          <p :class="$style.hintText">The offer will only be shown in the specified time</p>
        </div>
      </div>
    </template>
  </VTDropdownPill>
</template>

<script setup lang="ts">
import VTDropdownPill from "@/components/VTDropdownPill";
import DatePicker from "primevue/datepicker";
import type { Ref } from "vue";
import { ref, watch } from "vue";

interface InternalActiveTime {
  from: Date;
  to: Date;
}

interface PropsActiveTime {
  from: string;
  to: string;
}

/*
 Date Utils section
 */
const formatTime = (dateTime: Date) => {
  const formatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return new Date(dateTime).toLocaleTimeString("en-US", formatOptions).replace(/^24/, "00");
};

const isValidTime = (dateTime: Date) => {
  const time = formatTime(dateTime);
  const [hours, minutes] = time.split(":");
  return Number(hours) < 24 && Number(minutes) < 60;
};

const asDate = (time: string) => {
  const [hours, minutes] = time.split(":");
  return new Date(0, 0, 0, Number(hours), Number(minutes), 0);
};

const propsAsDate = (activeTime: PropsActiveTime) => ({
  from: asDate(activeTime.from),
  to: asDate(activeTime.to),
});

const props = defineProps<{ modelValue: PropsActiveTime; disabled: boolean }>();
const emit = defineEmits(["update"]);
const startTimeCalendarParentRef = ref<HTMLElement>(null);
const endTimeCalendarParentRef = ref<HTMLElement>(null);
const activeTime: Ref<InternalActiveTime> = ref({
  from: asDate(props.modelValue.from),
  to: asDate(props.modelValue.to),
});

watch(
  () => props.modelValue,
  (newValue: PropsActiveTime) => {
    activeTime.value = propsAsDate(newValue);
  }
);

const saveChanges = () => {
  emit("update", {
    from: formatTime(activeTime.value.from),
    to: formatTime(activeTime.value.to),
  });
};

const reset = () => {
  emit("update", {
    from: "00:00",
    to: "23:59",
  });
};
const discardChanges = () => {
  activeTime.value = {
    from: asDate(props.modelValue.from),
    to: asDate(props.modelValue.to),
  };
};

const isSelectedActiveTimeInvalid = () =>
  !isValidTime(activeTime.value.from) ||
  !isValidTime(activeTime.value.to) ||
  activeTime.value.from.getTime() >= activeTime.value.to.getTime();
</script>
<style module>
.wrapper {
  display: flex;
  flex-direction: row;
  overflow-x: hidden;
  align-items: center;
  gap: 8px;
}

.dropdownContainer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.row {
  display: flex;
  position: relative;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeText {
  font-weight: 700;
  font-size: 14px;
}

.hintText {
  color: var(--vd-color-text-light-2);
  font-size: 12px;
  font-weight: 400;
  text-align: start;
}

.fixedTopLeft {
  top: inherit !important;
  left: inherit !important;
}

.invalidText {
  color: #f56b6b;
  margin: 0;
  padding: 0;
}
</style>
